import { Roles } from '@omnea-digital/toolbox'

// TODO(Permissions): delete this file once every route's access is answered by the service.

/**
 * What a route's own gate could read before the Permissions Service. Nothing here says which records a
 * caller is assigned, so a rule needing that belongs on the `fga` arm.
 */
export type RoleAuthorizationContext = {
  session: {
    role: Roles
    /** Whether the caller may change configuration, which the session carries independently of role. */
    hasConfigPermissions: boolean
    /** Carried separately from the role, so an admin may still be restricted to reading. */
    isViewOnly: boolean
  }
  /** Organisation-wide, and true for every caller at once: these widen a rule by role, not by assignment. */
  orgFlags: {
    assigneePermissionsEnabled: boolean
    p2pAdminModeEnabled: boolean
  }
}

export type RoleAuthorizationRule = (context: RoleAuthorizationContext) => boolean

export const everyRole: RoleAuthorizationRule = () => true

/** Nobody: the action has no role a caller can hold, so only a grant from the service confers it. */
export const noRole: RoleAuthorizationRule = () => false

export const everyRoleExceptSuppliers: RoleAuthorizationRule = ({ session }) => session.role !== Roles.SUPPLIER

/** Any role that may act rather than only read — an admin is not required. */
export const nonViewOnly: RoleAuthorizationRule = ({ session }) => session.isViewOnly === false

export const adminsOnly: RoleAuthorizationRule = ({ session }) => session.role === Roles.ADMIN

/** An admin who may act, not only read — what a route hidden from view-only callers requires. */
export const nonViewerAdmins: RoleAuthorizationRule = (context) => adminsOnly(context) && context.session.isViewOnly === false

/** "Super admin" — the caller a settings route requires where its config area is not answered for. */
export const superAdmin: RoleAuthorizationRule = (context) => nonViewerAdmins(context) && context.session.hasConfigPermissions

/**
 * P2P_ADMIN_MODE, a frontend-only stop-gap: an admin without configuration permission may still reach
 * Workflows and Forms, and the editors beneath them.
 */
export const superAdminOrP2pAdmin: RoleAuthorizationRule = (context) =>
  superAdmin(context) || (nonViewerAdmins(context) && context.orgFlags.p2pAdminModeEnabled)

/**
 * The same stop-gap read the other way: where it is on, a destructive action an admin could otherwise
 * take requires configuration permission as well. Superseded by the record's own verb once the
 * organisation is answered for by the service.
 */
export const nonViewerAdminsUnlessP2pRestricted: RoleAuthorizationRule = (context) =>
  nonViewerAdmins(context) && (context.orgFlags.p2pAdminModeEnabled === false || context.session.hasConfigPermissions)

/**
 * The organisation flag widens by role rather than by assignment, which is the only approximation a
 * role rule can make — a list route has no record to check an assignment against. A caller assigned to
 * particular suppliers is admitted by a grant on the `fga` arm instead.
 *
 * The side menu is what this must agree with: it hides Suppliers from a plain user while the flag is
 * off.
 */
export const adminsOrAssigneeRoles: RoleAuthorizationRule = (context) =>
  context.orgFlags.assigneePermissionsEnabled ? everyRoleExceptSuppliers(context) : adminsOnly(context)
